import React, { useState } from "react";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { useContactForm } from "./ContactContext";
import { PHONE_DISPLAY, PHONE_TEL } from "../config";

const QUICK_LINKS = [
  { label: "Home", href: "/" },
  { label: "Solutions", href: "/#solutions" },
  { label: "Car Wash", href: "/industries/car-wash" },
  { label: "Parking", href: "/industries/parking" },
];

// Lo que se puede adaptar en el equipo del cliente: solo informativo, sin enlace.
const SERVICES = [
  "Car Wash Pay Stations",
  "Parking & Access Terminals",
  "Retail Checkout Kiosks",
  "Cash Handling & Deposits",
  "Custom Hardware Integration",
  "Software & API Integration",
  "Maintenance & Support",
];

const PhoneNumber = () => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText("+15127488298").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  };

  return (
    <div className="flex items-center gap-2 mb-5">
      <a
        href={PHONE_TEL}
        className="font-bold text-white text-base tracking-wide transition-colors duration-200 hover:text-white/80"
      >
        {PHONE_DISPLAY}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy phone number"
        className="text-[10px] font-medium transition-all duration-200"
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: copied ? "#86efac" : "rgba(255,255,255,0.6)" }}
      >
        {copied ? "✓ copied" : "copy"}
      </button>
    </div>
  );
};

export const Footer = () => {
  const { open } = useContactForm();
  const [email, setEmail] = useState("");
  const [modalType, setModalType] = useState(null);
  const [copied, setCopied] = useState(false);
  const emailBtnRef = React.useRef(null);

  const closeModal = () => setModalType(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      closeModal();
    }, 1200);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    open({ intent: "quote", email: email.trim(), source: "Footer" });
  };

  const PopUp = ({ buttonRef }) => {
    const value = "roberto@kinitas.com";
    const copyValue = value;
    const label = "Email";
    const [pos, setPos] = React.useState({ top: 0, left: 0 });

    React.useEffect(() => {
      if (!buttonRef?.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const popupWidth = 210;
      const margin = 12;
      let left = rect.left + rect.width / 2 - popupWidth / 2;
      if (left < margin) left = margin;
      if (left + popupWidth > window.innerWidth - margin) {
        left = window.innerWidth - popupWidth - margin;
      }
      const top = rect.top - 130;
      setPos({ top, left });
    }, [buttonRef]);

    return (
      <>
        <div className="fixed inset-0 z-40" onClick={closeModal} />
        <div
          className="fixed z-50"
          style={{
            top: `${pos.top}px`,
            left: `${pos.left}px`,
            animation: "popupFadeIn 0.18s ease forwards",
          }}
        >
          <div
            className="rounded-2xl px-5 py-4 text-center shadow-2xl border border-white/20"
            style={{
              width: "210px",
              background: "rgba(15, 30, 80, 0.85)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
            }}
          >
            <p className="text-white/50 text-[10px] uppercase tracking-widest mb-1">{label}</p>
            <p className="text-white font-medium text-sm mb-4">{value}</p>
            <button
              onClick={() => copyToClipboard(copyValue)}
              className="w-full py-2 rounded-xl text-xs font-semibold transition-all duration-200"
              style={{
                background: copied ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.15)",
                color: copied ? "#86efac" : "white",
                border: copied ? "1px solid rgba(34,197,94,0.4)" : "1px solid rgba(255,255,255,0.2)",
              }}
            >
              {copied ? "✓ Copied!" : "Copy"}
            </button>
          </div>
          <div className="flex justify-center -mt-[1px]">
            <div
              className="w-3 h-3 rotate-45 border-r border-b border-white/20"
              style={{ background: "rgba(15, 30, 80, 0.85)" }}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <footer
      id="footer"
      className="relative bg-[#1f3a70] text-white overflow-hidden"
    >
      {/* Background SVG */}
      <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">
        <img
          src="/images/bg-footer.svg"
          alt=""
          className="absolute top-0 left-0 h-full opacity-30 object-left object-cover"
        />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          {/* Col 1 - Brand */}
          <div>
            <img
              src="/kinitas-logo.svg"
              alt="Kinitas"
              className="w-32 h-auto brightness-0 invert mb-3"
            />
            <PhoneNumber />
            <p className="text-sm text-white/70 leading-relaxed">
              Kinitas builds self-service kiosks for businesses across America that are ready to run without an attendant.
            </p>

            {/* Social icons */}
            <div className="flex gap-4 mt-6">
              {/* Email */}
              <div className="relative">
                <button
                  ref={emailBtnRef}
                  onClick={() => setModalType(modalType === "email" ? null : "email")}
                  aria-label="Show email address"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300 flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                </button>
                {modalType === "email" && <PopUp buttonRef={emailBtnRef} />}
              </div>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/kinitas_pay/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Kinitas on Instagram"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300 flex items-center justify-center"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.975-.975 2.242-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.014 7.052.072 5.197.157 3.355.673 1.924 2.104.493 3.535-.023 5.377.072 7.232.014 8.332 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.855.601 3.697 2.032 5.128 1.431 1.431 3.273 1.947 5.128 2.032C8.332 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 1.855-.085 3.697-.601 5.128-2.032 1.431-1.431 1.947-3.273 2.032-5.128C23.986 15.668 24 15.259 24 12c0-3.259-.014-3.668-.072-4.948-.085-1.855-.601-3.697-2.032-5.128C20.465.673 18.623.157 16.768.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>

              {/* Phone */}
              <a
                href={PHONE_TEL}
                aria-label={`Call ${PHONE_DISPLAY}`}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300 flex items-center justify-center"
              >
                <PhoneIcon className="w-5 h-5" />
              </a>

              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/kinitas"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Kinitas on LinkedIn"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300 flex items-center justify-center"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.327-.025-3.037-1.849-3.037-1.849 0-2.132 1.445-2.132 2.939v5.667H9.358V9h3.414v1.561h.049c.476-.9 1.637-1.849 3.368-1.849 3.602 0 4.268 2.37 4.268 5.455v6.285zM5.337 7.433a2.062 2.062 0 110-4.125 2.062 2.062 0 010 4.125zM6.864 20.452H3.809V9h3.055v11.452z" />
                </svg>
              </a>
            </div>

            {/* Address */}
            <p className="text-xs text-white/50 mt-4 flex items-center gap-2">
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              WY, USA 82801
            </p>
          </div>

          {/* Col 2 - Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white/60 mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-white/70 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 - Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white/60 mb-5">
              Services
            </h4>
            <ul className="space-y-3">
              {SERVICES.map((item) => (
                <li key={item} className="text-sm text-white/70">
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 - Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-white/60 mb-5">
              Contact Info
            </h4>
            <p className="text-sm text-white/70 mb-5 leading-relaxed">
              Tell us about your site and a Kinitas specialist will call you within 24 hours.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-[260px]">
              <label htmlFor="footer-email" className="sr-only">Work email</label>
              <input
                id="footer-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your work email"
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/60 text-sm border border-white/20 focus:outline-none focus:border-white/50 transition-colors duration-200"
              />
              <button
                type="submit"
                className="w-full px-4 py-3 rounded-lg bg-white text-[#1a1f6c] font-medium text-sm hover:bg-gray-100 transition-colors duration-300"
              >
                Get a Quote
              </button>
            </form>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/20 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/70">© {new Date().getFullYear()} Kinitas USA, LLC.  All rights reserved. Wyoming, USA.</p>
          <div className="flex items-center gap-2 text-xs text-white/50">
            <a
              href="#"
              className="hover:text-white transition-colors duration-200"
            >
              Privacy Policy
            </a>
            <span>·</span>
            <a
              href="#"
              className="hover:text-white transition-colors duration-200"
            >
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
